/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';
import type {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { FormBuilder, FormControl, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { Subject, merge, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';

import { UserSelectors } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import { filterBoolean, workModelValidator } from '@recruitee/common';
import type { Requisition } from '@recruitee/requisitions-types';
import { deepEqual } from '@recruitee/utils';

import { RootPageDataService } from '../data/single-requisition/root-page-data.service';
import type { AvailableFieldsMap, RequisitionForm } from '../requisitions.types';
import { RequisitionFormDataAdapter } from './requisition-form-data.adapter';
import { RequisitionAvailableField, RequisitionFormFactory } from './requisition-form.factory';

export function userExistsValidator(users: Record<string, User | null>): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return { required: true };
    } else {
      return !users[control.value] || users[control.value]!.anonymizedAt
        ? { required: true }
        : null;
    }
  };
}

@Injectable()
export class RequisitionFormManagerService {
  public form: FormGroup<RequisitionForm>;
  public lastUsedRequisition: Requisition;
  public enabledValidation: boolean;
  public updated$: Observable<void>;

  private requisition$: Observable<Requisition>;
  private availableFieldsMap: AvailableFieldsMap;

  private ngOnDestroy$ = new Subject<void>();
  private formFactory = new RequisitionFormFactory();
  private formDataAdapter = new RequisitionFormDataAdapter();
  private _updated = new Subject<void>();
  private users: Record<string, User | null>;

  constructor(
    private fb: FormBuilder,
    protected rootData: RootPageDataService,
    private store: Store,
  ) {
    this.requisition$ = rootData.requisition$;
    this.updated$ = this._updated.asObservable();
    this.store
      .select(UserSelectors.entityMap)
      .pipe(takeUntil(this.ngOnDestroy$), filterBoolean)
      .subscribe(users => (this.users = users));
  }

  public get openingsFormArray() {
    return <FormArray>this.getControl('openings');
  }

  public getControl(name: string) {
    return this.form.get(name);
  }

  public initializeForm() {
    this.requisition$.pipe(take(1), takeUntil(this.ngOnDestroy$)).subscribe(requisition => {
      this.prepareAvailableFieldsMap(requisition);
      this.form = this.formFactory.buildFull(requisition, this.availableFieldsMap);
      this.lastUsedRequisition = requisition;
      this.handleHoursPerWeekValidationUpdate();
      this._updated.next();
    });
  }

  public fillForm() {
    this.requisition$.pipe(take(1), takeUntil(this.ngOnDestroy$)).subscribe(requisition => {
      this.prepareAvailableFieldsMap(requisition);
      this.form.patchValue(this.formDataAdapter.getFormValues(requisition));

      this.form.setControl(
        'openings',
        this.fb.array(
          requisition.openings.map(opening =>
            this.formFactory.buildOpening({
              availableOpeningsFields: this.availableFieldsMap['openings'],
              isSaved: true,
              opening,
            }),
          ),
          [
            ...(this.openingsFormArray.hasValidator(Validators.required)
              ? [Validators.required]
              : []),
          ],
        ),
      );

      if (this.availableFieldsMap.location?.locations) {
        const controls = requisition.locationIds?.length
          ? requisition.locationIds.map(
              locationId => new FormControl(locationId, { nonNullable: true }),
            )
          : [];
        this.form.setControl(
          'locationIds',
          this.fb.array(controls, [
            ...(this.getControl('locationIds')?.hasValidator(Validators.required)
              ? [Validators.required]
              : []),
          ]),
        );
      }

      const requisitionIdChanged = this.lastUsedRequisition?.id !== requisition.id;
      this.lastUsedRequisition = requisition;

      if (requisitionIdChanged) {
        this.toggleFieldsRequired(false);
        this._updated.next();
        return;
      }

      if (this.enabledValidation) this.toggleFieldsRequired();
      this._updated.next();
    });
  }

  public updateForm() {
    this.requisition$.pipe(take(1), takeUntil(this.ngOnDestroy$)).subscribe(requisition => {
      const { added, removed } = this.changedRequisitionFields(requisition);

      if (added.length) {
        added.forEach(field =>
          this.form.addControl(field, this.formFactory.buildControl(field, requisition)),
        );
      }

      if (removed.length) {
        removed.forEach(field => this.form.removeControl(field));
      }

      this.fillForm();
    });
  }

  public getFormValue() {
    const { workModel, ...formValue } = this.form.getRawValue();
    return {
      ...formValue,
      openings: formValue.openings?.map(({ saved, ...restProperties }) => restProperties),
      ...(workModel ? { ...workModel } : {}),
    };
  }

  public toggleFieldsRequired(required = true, formGroup?: FormGroup): void {
    const form = formGroup ? formGroup : <FormGroup>this.form;
    Object.keys(form.controls).forEach(key => {
      if (!form.controls[key].disabled) {
        if (key === 'salary') {
          this.toggleFieldsRequired(required, <FormGroup>form.controls[key]);
        } else {
          if (key === 'departmentId') {
            form.controls[key].updateValueAndValidity({ emitEvent: true });
            return;
          }

          if (required) {
            if (key === 'openings') {
              this.toggleOpeningsFieldsRequired(true);
            }
            if (key === 'workModel') {
              form.controls[key]!.addValidators(workModelValidator);
            }
            if (key === 'hiringManagerId' || key === 'recruiterId') {
              form.controls[key]!.addValidators(userExistsValidator(this.users));
            }
            form.controls[key]!.addValidators(Validators.required);
          } else {
            if (key === 'openings') {
              this.toggleOpeningsFieldsRequired(false);
            }
            if (key === 'workModel') {
              form.controls[key]!.removeValidators(workModelValidator);
            }
            if (key === 'hiringManagerId' || key === 'recruiterId') {
              form.controls[key]!.clearValidators();
            }
            form.controls[key].removeValidators(Validators.required);
          }
          form.controls[key].updateValueAndValidity();
          form.controls[key].markAsTouched();
        }
      }
    });
  }

  public noChanges(): boolean {
    return !this.form || this.form.pristine;
  }

  public hasRequisitionFieldsChanged(requisition: Requisition) {
    const previousRequisitionFields = this.filterAvailableRequisitionFields(
      Object.keys(this.lastUsedRequisition),
    );
    const currentRequisitionFields = this.filterAvailableRequisitionFields(
      Object.keys(requisition),
    );
    return (
      !deepEqual(previousRequisitionFields, currentRequisitionFields) ||
      !deepEqual(this.lastUsedRequisition.form.fields, requisition.form.fields)
    );
  }

  public hasRequisitionStatusChanged(requisition: Requisition) {
    return this.lastUsedRequisition ? this.lastUsedRequisition.status !== requisition.status : true;
  }

  public keepValidation(keep: boolean) {
    this.enabledValidation = keep;
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private changedRequisitionFields(requisition: Requisition) {
    const previousRequisitionFields = this.getRequisitionFields(this.lastUsedRequisition);
    const currentRequisitionFields = this.getRequisitionFields(requisition);

    return {
      added: currentRequisitionFields.filter(field => !previousRequisitionFields.includes(field)),
      removed: previousRequisitionFields.filter(field => !currentRequisitionFields.includes(field)),
    };
  }

  private getRequisitionFields(requisition: Requisition) {
    let fields = Object.keys({ ...requisition });
    const hasWorkModel =
      fields.includes(RequisitionAvailableField.Remote) &&
      fields.includes(RequisitionAvailableField.OnSite) &&
      fields.includes(RequisitionAvailableField.Hybrid);

    if (hasWorkModel) {
      fields = [
        ...fields.filter(
          field =>
            field !== RequisitionAvailableField.Remote &&
            field !== RequisitionAvailableField.Hybrid &&
            field !== RequisitionAvailableField.OnSite,
        ),
        RequisitionAvailableField.WorkModel,
      ];
    }

    return fields;
  }

  private prepareAvailableFieldsMap(requisition: Requisition) {
    const availableFormFields = requisition.form.fields;
    this.availableFieldsMap = this.formDataAdapter.getAvailableFields(availableFormFields);
  }

  private toggleOpeningsFieldsRequired(required: boolean): void {
    const isReasonRequired = this.availableFieldsMap['openings']['reason'];
    const isAnticipatedStartDateRequired =
      this.availableFieldsMap['openings']['anticipated_start_date'];
    const isExplanationRequired = this.availableFieldsMap['openings']['explanation'];

    this.openingsFormArray.controls.forEach(openingGroup => {
      if (required) {
        if (isReasonRequired) openingGroup.get('reason')?.addValidators(Validators.required);
        if (isAnticipatedStartDateRequired)
          openingGroup.get('anticipatedStartDate')?.addValidators(Validators.required);
        if (isExplanationRequired)
          openingGroup.get('explanation')?.addValidators(Validators.required);
      } else {
        if (isReasonRequired) openingGroup.get('reason')?.removeValidators(Validators.required);
        if (isAnticipatedStartDateRequired)
          openingGroup.get('anticipatedStartDate')?.removeValidators(Validators.required);
        if (isExplanationRequired)
          openingGroup.get('explanation')?.removeValidators(Validators.required);
      }
      openingGroup.get('reason')?.updateValueAndValidity();
      openingGroup.get('anticipatedStartDate')?.updateValueAndValidity();
      openingGroup.get('explanation')?.updateValueAndValidity();
    });
  }

  private handleHoursPerWeekValidationUpdate(): void {
    const minHoursControl = this.form.controls.minHoursPerWeek;
    const maxHoursControl = this.form.controls.maxHoursPerWeek;

    if (minHoursControl && maxHoursControl) {
      merge(minHoursControl.valueChanges, maxHoursControl.valueChanges)
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(() => {
          minHoursControl.updateValueAndValidity({ emitEvent: false });
          maxHoursControl.updateValueAndValidity({ emitEvent: false });
        });
    }
  }

  private filterAvailableRequisitionFields(fields: string[]) {
    return fields.filter(
      field =>
        !!Object.values(RequisitionAvailableField).find(
          requisitionField => requisitionField === field,
        ),
    );
  }
}
